import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { sendPedigreeFinalReviewData } from './SendPedigree';
import DisplayTable from '../../components/DisplayTable.tsx';
import {
  CustomerLocationAddress,
  DisplayTableData,
  TradingPartnerAddress,
} from '../../common/types/models';
import { MedAPI } from '../../common/api/med.ts';

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  data: sendPedigreeFinalReviewData;
  send: (event: MouseEvent<HTMLAnchorElement>) => void;
}

const FinalReviewModal = ({ isOpen, setIsOpen, data, send }: IProps) => {
  const modal = useRef<HTMLDivElement>(null);

  const [displayTableData, setDisplayTableData] = useState<DisplayTableData[]>(
    []
  );

  useEffect(() => {
    const { current } = modal;
    if (current) {
      M.Modal.init(current, {
        onCloseEnd: () => setIsOpen(false),
      });
      return () => M.Modal.getInstance(current)?.destroy();
    }
  }, [modal, setIsOpen]);

  const getModal = useCallback(
    () => (modal.current ? M.Modal.getInstance(modal.current) : null),
    []
  );
  const openModal = useCallback(() => getModal()?.open(), [getModal]);
  const closeModal = useCallback(() => getModal()?.close(), [getModal]);

  useEffect(() => {
    if (isOpen) openModal();
    else closeModal();
  }, [isOpen, openModal, closeModal]);

  useEffect(() => {
    if (!data) return;
    const {
      customerLocation,
      customerLocationAddress,
      tradingPartner,
      tradingPartnerAddress,
      meds,
      additionalData,
    } = data;

    const getCustomerLocationAddress = (address?: CustomerLocationAddress) => {
      const arr: DisplayTableData[] = [];
      if (!address) return arr;
      const { addressLineTwo, addressLineThree, city, state, zip } = address;
      if (addressLineTwo) arr.push({ label: 'Address', value: addressLineTwo });
      if (addressLineThree)
        arr.push({ label: <span>&nbsp;</span>, value: addressLineThree });
      if (city)
        arr.push({
          label: <span>&nbsp;</span>,
          value: `${city} ${state ? `, ${state.code}` : ''} ${zip}`,
        });

      return arr;
    };

    const getTradingPartnerAddress = (address?: TradingPartnerAddress) => {
      const arr: DisplayTableData[] = [];
      if (!address) return arr;
      const { addressLineOne, addressLineTwo, city, state, postalCode } =
        address;
      if (addressLineOne) arr.push({ label: 'Address', value: addressLineOne });
      if (addressLineTwo)
        arr.push({ label: <span>&nbsp;</span>, value: addressLineTwo });
      if (city)
        arr.push({
          label: <span>&nbsp;</span>,
          value: `${city} ${state ? `, ${state.code}` : ''} ${postalCode}`,
        });

      return arr;
    };

    const getMeds = (
      meds?: MedAPI.Response.ReadOneBySerialNumberPedigree[]
    ) => {
      const arr: DisplayTableData[] = [];
      if (!meds) return arr;

      meds.forEach((med, index) =>
        arr.push({
          label: index === 0 ? 'Meds' : <span>&nbsp;</span>,
          value: `${med.med.serialNumber.split('.').pop()} - ${med.redbookDetail.medName}`,
        })
      );

      return arr;
    };

    setDisplayTableData([
      { label: 'Send From', value: customerLocation?.name || '' },
      ...getCustomerLocationAddress(customerLocationAddress),
      { label: 'Send To', value: tradingPartner?.name || '' },
      ...getTradingPartnerAddress(tradingPartnerAddress),
      ...getMeds(meds),
      { label: 'PO Number', value: additionalData?.poNumber || '' },
      { label: 'Invoice Number', value: additionalData?.invoiceNumber || '' },
      {
        label: 'Advance Ship Notice',
        value: additionalData?.advanceShipNotice || '',
      },
    ]);
  }, [data]);

  return (
    <div ref={modal} className="modal">
      <div className="modal-content">
        <div className="row">
          <h5 className="center">Review Pedigree</h5>
          <div className="divider" />
        </div>
        <div className="row">
          <DisplayTable data={displayTableData} />
        </div>
        <div className="row">
          <div className="col s12 m4 offset-m4">
            <a
              href="/"
              className="btn-small blue white-text waves-effect waves-light col s12"
              onClick={send}
            >
              Send Pedigree
            </a>
          </div>
          <div className="col s12 m4">
            <a
              href="/"
              className="btn-small white blue-text waves-effect waves-light col s12"
              onClick={e => {
                e.preventDefault();
                setIsOpen(false);
              }}
            >
              Cancel
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalReviewModal;
